// =====================================================================
// esignService (e-sign / S104 F1) — chamadas do módulo de assinatura de contratos.
// Backend: /esign/*. Gating por tenant (esign_enabled) — o front lê /esign/config.
// =====================================================================
import api from "./api";

// { enabled } — gating do menu Contratos
export const getEsignConfig = async () => (await api.get("/esign/config")).data;

// -------------------------------- Documentos -------------------------
export const listEsignDocuments = async (params = {}) =>
  (await api.get("/esign/documents", { params })).data; // { documents, count, ... }

export const getEsignDocument = async (id) =>
  (await api.get(`/esign/documents/${id}`)).data; // { document, signers, events }

export const createEsignFromTemplate = async (payload) =>
  (await api.post("/esign/documents/template", payload)).data;

// formData: file (PDF) + campos (patientId, title, signers[JSON])
export const createEsignFromUpload = async (formData) =>
  (await api.post("/esign/documents/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })).data;

// médico assina (ICP). payload opcional { professionalId }
export const signEsignAsDoctor = async (id, payload = {}) =>
  (await api.post(`/esign/documents/${id}/sign`, payload)).data;

export const cancelEsignDocument = async (id) =>
  (await api.post(`/esign/documents/${id}/cancel`)).data;

// [F2] gera o(s) link(s) de assinatura do(s) paciente(s) pendente(s) — { links:[{signerId,name,contactWa,url}] }
export const sendEsignForSignature = async (id) =>
  (await api.post(`/esign/documents/${id}/send`)).data;

// baixa o PDF (assinado se houver) como blob e abre em nova aba (auth/Bearer)
export const openEsignPdf = async (id) => {
  const resp = await api.get(`/esign/documents/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// [F3] baixa o MANIFESTO/prova (PDF) como blob e abre em nova aba (auth/Bearer)
export const openEsignManifest = async (id) => {
  const resp = await api.get(`/esign/documents/${id}/manifest`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// [F3] link PÚBLICO de verificação (o mesmo do QR/rodapé do manifesto). Base NEUTRA (white-label).
export const esignVerifyUrl = (code) =>
  `https://api.genius.med.br/verificar/esign/${encodeURIComponent(code || "")}`;

// [S107] Certificado ICP self-service (o médico sobe o próprio .pfx pela UI)
export const listProfessionalsCert = async () =>
  (await api.get("/esign/certs")).data; // { professionals:[{id,name,hasCert,cn,validUntil}] }

export const uploadProfessionalCert = async (professionalId, file, password) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("password", password);
  return (await api.post(`/esign/professionals/${professionalId}/cert`, fd, {
    headers: { "Content-Type": "multipart/form-data" }
  })).data;
};

export const removeProfessionalCert = async (professionalId) =>
  (await api.delete(`/esign/professionals/${professionalId}/cert`)).data;

// -------------------------------- Modelos ----------------------------
export const listEsignTemplates = async () =>
  (await api.get("/esign/templates")).data; // { templates }

export const getEsignTemplate = async (id) =>
  (await api.get(`/esign/templates/${id}`)).data;

export const upsertEsignTemplate = async (payload) =>
  (await api.post("/esign/templates", payload)).data;

export const deleteEsignTemplate = async (id) =>
  (await api.delete(`/esign/templates/${id}`)).data;

// mensagens amigáveis dos erros do e-sign (fallback pro texto do backend)
export const esignErrorMessage = (err) => {
  const code = err && err.response && err.response.data && err.response.data.error;
  const MAP = {
    ERR_ESIGN_DISABLED: "Módulo de contratos não está habilitado para esta clínica.",
    ERR_ESIGN_DOC_NOT_FOUND: "Documento não encontrado.",
    ERR_ESIGN_TEMPLATE_NOT_FOUND: "Modelo não encontrado.",
    ERR_ESIGN_NO_DOCTOR_SIGNER: "Este documento não tem um médico como signatário pendente.",
    ERR_ESIGN_NO_CERT: "O médico não tem certificado ICP configurado no sistema.",
    ERR_ESIGN_SIGN_FAILED: "Falha ao assinar (verifique o certificado/senha).",
    ERR_ESIGN_PDF_MISSING: "O PDF do documento não foi encontrado.",
    ERR_ESIGN_DOC_DONE: "Documento já está assinado/finalizado.",
    ERR_ESIGN_DOC_CANCELLED: "Documento cancelado.",
    ERR_ESIGN_FILE_REQUIRED: "Selecione o PDF do contrato.",
    ERR_ESIGN_FILE_TYPE: "Envie um arquivo PDF.",
    ERR_ESIGN_TEMPLATE_NAME: "Informe o nome do modelo.",
    ERR_ESIGN_TEMPLATE_BODY: "O corpo do modelo não pode ficar vazio.",
    ERR_ESIGN_NO_MANIFEST: "O manifesto/prova ainda não foi gerado para este documento.",
    ERR_ESIGN_EXPIRED: "Este documento expirou (passou da validade) e não pode mais ser assinado.",
    ERR_ESIGN_NO_PENDING_PATIENT: "Não há paciente pendente de assinatura neste documento.",
    ERR_ESIGN_CERT_INVALID: "Certificado ou senha inválidos. Confira o arquivo .pfx e a senha.",
    ERR_ESIGN_CERT_FILE_REQUIRED: "Selecione o arquivo do certificado (.pfx).",
    ERR_ESIGN_CERT_PASS_REQUIRED: "Informe a senha do certificado.",
    ERR_ESIGN_CERT_TYPE: "Envie um arquivo .pfx ou .p12.",
    ERR_ESIGN_PROF_NOT_FOUND: "Profissional não encontrado.",
    ERR_FISCAL_ENC_KEY_UNSET: "Chave de criptografia do servidor não configurada (FISCAL_ENC_KEY)."
  };
  if (code && MAP[code]) return MAP[code];
  return (err && err.response && err.response.data && err.response.data.error) || "Não foi possível concluir a operação.";
};
